import { useEffect, useRef } from "react";
import { createChart, ColorType } from "lightweight-charts";

interface Candle {
  time: string | number;
  open: number;
  close: number;
  high: number;
  low: number;
  volume?: number;
}

interface KlineChartProps {
  data: Candle[];
  height?: number;
}

export default function KlineChart({ data, height = 380 }: KlineChartProps) {
  // Deduplicate by time — lightweight-charts requires strictly ascending unique times
  const deduped = data.filter((d, i, arr) => i === 0 || d.time !== arr[i - 1].time);

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current || deduped.length < 2) return;
    const container = containerRef.current;
    const w = container.clientWidth || 400;

    const chart = createChart(container, {
      width: w,
      height,
      layout: {
        background: { type: ColorType.Solid, color: "#ffffff" },
        textColor: "#64748b",
        fontSize: 10,
      },
      grid: {
        vertLines: { color: "#f1f5f9" },
        horzLines: { color: "#f1f5f9" },
      },
      rightPriceScale: {
        borderColor: "#e2e8f0",
        scaleMargins: { top: 0.05, bottom: 0.3 },
      },
      timeScale: {
        borderColor: "#e2e8f0",
        timeVisible: true,
        secondsVisible: false,
        tickMarkFormatter: (time: number) => {
          if (typeof time !== 'number') return String(time);
          const d = new Date(time * 1000);
          const isIntraday = deduped.length >= 2 && typeof deduped[0].time === 'number' &&
            (Number(deduped[1].time) - Number(deduped[0].time)) < 3600;
          if (isIntraday) {
            return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
          }
          return `${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
        },
      },
      crosshair: { mode: 0 },
      handleScroll: false,
      handleScale: false,
    });

    // Candlestick series
    const candleSeries = chart.addCandlestickSeries({
      upColor: "#e11d48",
      downColor: "#10b981",
      borderUpColor: "#e11d48",
      borderDownColor: "#10b981",
      wickUpColor: "#e11d48",
      wickDownColor: "#10b981",
    });
    candleSeries.setData(
      deduped.map((d) => ({
        time: d.time,
        open: d.open,
        high: d.high,
        low: d.low,
        close: d.close,
      }))
    );

    // Volume
    const volumeSeries = chart.addHistogramSeries({
      priceFormat: { type: "volume", precision: 0 },
      priceScaleId: "vol",
    });
    chart.priceScale("vol").applyOptions({
      scaleMargins: { top: 0.85, bottom: 0 },
      visible: false,
    });
    volumeSeries.setData(
      deduped.map((d) => ({
        time: d.time,
        value: (d.volume || 0) / 10000,
        color: d.close >= d.open ? "rgba(225,29,72,0.3)" : "rgba(16,185,129,0.3)",
      }))
    );

    // MA5
    if (deduped.length >= 5) {
      const ma5Data = [];
      for (let i = 4; i < deduped.length; i++) {
        let sum = 0;
        for (let j = i - 4; j <= i; j++) sum += deduped[j].close;
        ma5Data.push({ time: deduped[i].time, value: +(sum / 5).toFixed(2) });
      }
      if (ma5Data.length > 0) {
        const ma5 = chart.addLineSeries({ color: "#6366f1", lineWidth: 1 });
        ma5.setData(ma5Data);
      }
    }

    // MA20
    if (deduped.length >= 20) {
      const ma20Data = [];
      for (let i = 19; i < deduped.length; i++) {
        let sum = 0;
        for (let j = i - 19; j <= i; j++) sum += deduped[j].close;
        ma20Data.push({ time: deduped[i].time, value: +(sum / 20).toFixed(2) });
      }
      if (ma20Data.length > 0) {
        const ma20 = chart.addLineSeries({ color: "#f59e0b", lineWidth: 1 });
        ma20.setData(ma20Data);
      }
    }

    chart.timeScale().fitContent();

    return () => {
      chart.remove();
    };
  }, [data, height]);

  if (deduped.length < 2) {
    return (
      <div className="flex items-center justify-center h-[200px] bg-background-50 rounded-lg">
        <p className="text-xs text-foreground-400">暂无 K 线数据</p>
      </div>
    );
  }

  return <div ref={containerRef} className="w-full" />;
}
